import { Skeleton } from '@/components/ui/app-skeleton';
import { CHART_HEIGHT_CLASS } from '@/config/chart';
import { DETAIL_SIDEBAR_W_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';

// Route-level Suspense fallback for `/winners/<mint>`. Winner detail renders
// through the SAME `render-market-detail` component as the market-detail page,
// so this mirrors that page's loading skeleton (size-24 cover, canonical
// CHART_HEIGHT_CLASS chart with the mode-toggle + interval row BELOW it) and
// adds the winner-only "RESOLVED FROM MARKET" recap card at the foot of the
// left column. Above the fold is detailed (header, chart, trade form); below
// the fold is simple blocks sized to the live heights.
export default function WinnerDetailLoading(): React.ReactNode {
  return (
    <div>
      <div className="flex gap-6">
        {/* Left column — header, chart, tabbed section, resolved recap */}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {/* Header — size-24 cover + (title · ticker) column, market-cap right. */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <Skeleton className="size-24 shrink-0 rounded-card" />
              <div className="flex min-w-0 flex-col gap-2">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="h-5 w-32" />
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              <Skeleton className="h-7 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>

          {/* Price chart — canvas at CHART_HEIGHT_CLASS, mode-toggle + interval
              row BELOW it. */}
          <div className="flex flex-col gap-3">
            <Skeleton className={cn(CHART_HEIGHT_CLASS, 'w-full rounded-card')} />
            <div className="flex items-center justify-between gap-2">
              <Skeleton className="h-9 w-40 rounded-pill" />
              <Skeleton className="h-9 w-28 rounded-pill" />
            </div>
          </div>

          {/* Mobile/tablet — actions + stats (desktop shows these in the sidebar). */}
          <div className="flex flex-col gap-3 lg:hidden">
            <Skeleton className="h-12 w-full rounded-card" />
            <Skeleton className="h-32 w-full rounded-card" />
          </div>

          {/* Tabbed section (Trades / Holders / Theses) — one simple block. */}
          <Skeleton className="h-64 w-full rounded-card" />

          {/* "RESOLVED FROM MARKET" recap card — one simple block. */}
          <Skeleton className="h-64 w-full rounded-card" />
        </div>

        {/* Right column — desktop only: trade form + stats + about token. */}
        <div
          className={cn(
            DETAIL_SIDEBAR_W_CLASS,
            'hidden shrink-0 flex-col gap-3 self-start lg:flex',
          )}
        >
          <Skeleton className="h-96 w-full rounded-card" />
          <Skeleton className="h-32 w-full rounded-card" />
          <Skeleton className="h-40 w-full rounded-card" />
        </div>
      </div>
    </div>
  );
}
